import "./Support.scss";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux"; // Add Redux imports
import ViewIcon from "./../../assets/show-user.svg";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  TextField,
  useMediaQuery,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Modal,
  Box,
  Typography,
  Button,
  TextareaAutosize,
  IconButton,
  Tooltip,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";

import api from "../../utils/api";
import { QuestionColumns } from "../../SourceData";
import { setNotificationCount } from "../../redux/notificationsSlice"; // Add notification action
import { usePermissions } from "./../../hooks/usePermissions";
import { toast } from "react-toastify";

const Support = () => {
  const { hasPermission } = usePermissions();
  const dispatch = useDispatch(); // Add dispatch hook
  const isMobile = useMediaQuery("(max-width:767px)");

  const [query, setQuery] = useState("");
  const [type, setType] = useState("");
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);

  // New states for modal
  const [openModal, setOpenModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Listen to notification count changes from Redux
  const notificationCount = useSelector(
    (state) => state.notifications?.counts?.supportQuestions || 0
  );

  // Handle view question click
  const handleViewQuestionClick = (question) => {
    // Check view permission
    if (!hasPermission(91)) {
      toast.error("ليس لديك صلاحية لعرض تفاصيل الاستفسار");
      return;
    }
    handleOpenModal(question);
  };

  // Handle update note
  const handleUpdateNote = async () => {
    // Check edit permission
    if (!hasPermission(92)) {
      toast.error("ليس لديك صلاحية لتعديل طلب الدعم بملاحظات");
      return;
    }
    handleSubmitNote();
  };

  useEffect(() => {
    const getData = async () => {
      // Check view permission before fetching data
      if (!hasPermission(91)) {
        toast.error("ليس لديك صلاحية لعرض جميع طلبات الدعم الفني");
        return;
      }

      setLoading(true);
      try {
        const params = new URLSearchParams({
          // page: page + 1,
          // limit: pageSize,
        });

        if (query.length >= 2) {
          params.append("q", query);
        }

        if (type) {
          params.append("type", type);
        }

        const res = await api.get(`/support-questions?${params.toString()}`);

        // Updated to match the new response format
        const responseData = res.data.data;
        const totalCount = res?.data?.totalCount || 0;

        setData(responseData);
        setTotalRows(totalCount);

        // Update Redux store with notification count
        // Count only questions without notes for notifications
        const questionsWithoutNotes = responseData.filter(
          (question) => !question.notes || question.notes.trim() === ""
        );

        dispatch(
          setNotificationCount({
            key: "supportQuestions",
            count: questionsWithoutNotes.length,
          })
        );
      } catch (error) {
        console.error("Error fetching support questions:", error);
        toast.error(
          "فشل في جلب استفسارات الدعم الفني. يرجى المحاولة مرة أخرى."
        );
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      if (query.length >= 2 || query.length === 0) {
        getData();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [query, type, page, pageSize, dispatch]); // Added dispatch to dependencies

  // Effect to refetch when notification count changes (triggered by WebSocket middleware)
  useEffect(() => {
    // Only refetch if the count changed and we're not already loading
    if (notificationCount >= 0 && !loading) {
      // This could be triggered by external updates, but we want to avoid infinite loops
      // So we'll only log for now, actual refetch will happen through other triggers
      console.log("Support questions notification count changed via WebSocket");
    }
  }, [notificationCount, loading]);

  const handlePaginationModelChange = (model) => {
    setPage(model.page);
    setPageSize(model.pageSize);
  };

  const handleOpenModal = (question) => {
    setSelectedQuestion(question);
    setNote(question.notes || "");
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedQuestion(null);
    setNote("");
  };

  const handleSubmitNote = async () => {
    if (!selectedQuestion) return;

    setSubmitting(true);
    try {
      await api.patch(`/support-questions/${selectedQuestion._id}`, {
        notes: note,
      });

      // Update the data in the table
      const updatedData = data.map((item) =>
        item._id === selectedQuestion._id ? { ...item, notes: note } : item
      );
      setData(updatedData);

      // Update notification count after adding note
      const questionsWithoutNotes = updatedData.filter(
        (question) => !question.notes || question.notes.trim() === ""
      );

      dispatch(
        setNotificationCount({
          key: "supportQuestions",
          count: questionsWithoutNotes.length,
        })
      );

      toast.success("تم حفظ الملاحظات بنجاح");
      handleCloseModal();
    } catch (error) {
      console.error("Error updating note:", error);
      toast.error("فشل في حفظ الملاحظات. يرجى المحاولة مرة أخرى.");
    } finally {
      setSubmitting(false);
    }
  };

  const actionColumn = [
    {
      field: "contacted",
      headerName: "تم التواصل",
      width: 120,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const hasNotes = Boolean(params.row.notes);
        return hasNotes ? (
          <CheckCircleIcon style={{ color: "green" }} />
        ) : (
          <CancelIcon style={{ color: "red" }} />
        );
      },
    },
    {
      field: "action",
      headerName: "",
      width: 200,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const canView = hasPermission(87);
        const canDelete = hasPermission(89);

        return (
          <div
            className="cell-action"
            style={{ display: "flex", gap: "8px", justifyContent: "center" }}
          >
            {/* View Button */}
            <Tooltip
              title={
                canView
                  ? "عرض تفاصيل الاستفسار"
                  : "ليس لديك صلاحية لعرض تفاصيل الاستفسار"
              }
            >
              <div
                className="cell-btn"
                onClick={() => handleViewQuestionClick(params.row)}
                style={{
                  cursor: canView ? "pointer" : "not-allowed",
                  opacity: canView ? 1 : 0.5,
                }}
              >
                <img src={ViewIcon} alt="عرض" />
              </div>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  // Modal style
  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isMobile ? "90%" : 600,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    direction: "rtl",
  };

  return (
    <div className="support">
      <div className="support-header">
        <p className="title">
          الدعم الفني
          {notificationCount > 0 && (
            <span className="notification-badge">({notificationCount})</span>
          )}
        </p>

        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder="ابحث..."
              onChange={(e) => setQuery(e.target.value)}
              sx={{
                width: isMobile ? "100%" : "380px",
                borderRadius: "8px",
                marginRight: "15px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl
              sx={{
                width: isMobile ? "100%" : "180px",
              }}
            >
              <InputLabel
                htmlFor="outlined-adornment-password"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.75)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                نوع الاستفسار
              </InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                value={type}
                onChange={(e) => setType(e.target.value)}
                displayEmpty={true}
                sx={{ borderRadius: "8px", height: "48px" }}
              >
                <MenuItem value="">الجميع</MenuItem>
                <MenuItem value="general_inquiry">استفسار عام</MenuItem>
                <MenuItem value="technical_support">دعم فني</MenuItem>
                <MenuItem value="suggestion">اقتراح</MenuItem>
                <MenuItem value="complaint">شكوى</MenuItem>
                <MenuItem value="other">أخرى</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
      </div>

      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={[...QuestionColumns, ...actionColumn]}
          rows={data.map((row, index) => ({
            ...row,
            id: row._id, // Use _id as id for DataGrid
            index: page * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          paginationMode="server"
          rowCount={totalRows}
          pageSizeOptions={[10, 25, 50]}
          paginationModel={{
            page,
            pageSize,
          }}
          onPaginationModelChange={handlePaginationModelChange}
        />
      </div>

      {/* Question Details Modal */}
      {hasPermission(87) && (
        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="question-modal-title"
        >
          <Box sx={modalStyle}>
            {selectedQuestion && (
              <>
                <Typography
                  id="question-modal-title"
                  variant="h6"
                  component="h2"
                  sx={{
                    mb: 3,
                    textAlign: "center",
                    fontWeight: "bold",
                    color: "#1976d2",
                    borderBottom: "1px solid #e0e0e0",
                    paddingBottom: "16px",
                  }}
                >
                  تفاصيل الاستفسار
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mb: 0.5 }}
                  >
                    الرسالة:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      p: 1.5,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      backgroundColor: "#f8f9fa",
                      minHeight: "60px",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {selectedQuestion.message}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mb: 0.5 }}
                  >
                    الملاحظات:
                  </Typography>
                  <TextareaAutosize
                    aria-label="notes"
                    placeholder="أضف ملاحظاتك هنا..."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    disabled={!hasPermission(88)}
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #e0e0e0",
                      minHeight: "100px",
                      direction: "rtl",
                      fontFamily: "inherit",
                      fontSize: "14px",
                      backgroundColor: hasPermission(88) ? "white" : "#f5f5f5",
                      cursor: hasPermission(88) ? "text" : "not-allowed",
                    }}
                  />
                  {!hasPermission(88) && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#666",
                        fontStyle: "italic",
                        mt: 0.5,
                        display: "block",
                      }}
                    >
                      ليس لديك صلاحية لتعديل الملاحظات
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
                >
                  <Button variant="outlined" onClick={handleCloseModal}>
                    {hasPermission(88) ? "إلغاء" : "إغلاق"}
                  </Button>
                  {hasPermission(88) && (
                    <Button
                      variant="contained"
                      onClick={handleUpdateNote}
                      disabled={submitting}
                    >
                      {submitting ? "جاري الحفظ..." : "حفظ الملاحظات"}
                    </Button>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Modal>
      )}
    </div>
  );
};

export default Support;
